import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { GET_VACATION_STATS, GET_EMPLOYEE_LEAVES, GET_LEAVE_TYPES, GET_DISABILITY_DEGREES, GET_ROLES_BASIC } from '../../graphql/queries';
import { usePermission } from '../../hooks/usePermission';
import { useNavigate } from 'react-router-dom';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  login: string;
  pin: string;
  employeeCode?: string;
  role: string;
  roleId?: string | null;
  roleName?: string | null;
  roleIds?: string[];
  roleNames?: string[] | null;
  isActive: boolean;
  department?: string;
  departmentId?: string;
  position?: {
    id: string;
    name: string;
    department?: string;
  };
  shift?: {
    id: string;
    name: string;
  };
  contractType?: {
    id: string;
    displayName: string;
    hasOvertimePay: boolean;
  };
  disabilityDegreeId?: string;
  disabilityDegree?: {
    id: string;
    name: string;
    maxWorkHoursPerDay?: number;
    breakDurationMinutes?: number;
    additionalBreakMinutes?: number;
  };
  hireDate: string;
  terminationDate?: string | null;
  autoCloseEnabled?: boolean;
  autoCloseType?: string;
  autoCloseTime?: string;
  autoCloseAfterHours?: number;
}

interface Position {
  id: string;
  name: string;
  department?: string;
}

interface Shift {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface Department {
  id: string;
  name: string;
}

interface ContractTypeOption {
  id: string;
  displayName: string;
  hasOvertimePay: boolean;
}

interface EmployeeDialogProps {
  employee: Employee | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string | null, data: any) => Promise<void>;
  positions: Position[];
  shifts: Shift[];
  departments: Department[];
  contractTypes: ContractTypeOption[];
  currentUserRole?: string;
}

export const EmployeeDialog: React.FC<EmployeeDialogProps> = ({
  employee,
  open,
  onClose,
  onSave,
  positions,
  shifts,
  departments,
  contractTypes,
  currentUserRole
}) => {
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: disabilityData } = useQuery(GET_DISABILITY_DEGREES, { fetchPolicy: 'cache-first' });
  const disabilityDegrees: Array<{ id: string; name: string; isDefault: boolean; isActive: boolean; maxWorkHoursPerDay: number; breakDurationMinutes: number; additionalBreakMinutes: number }> = disabilityData?.disabilityDegrees || [];
  const defaultDisabilityDegreeId = disabilityDegrees.find(d => d.isDefault)?.id || disabilityDegrees[0]?.id || '';

  const defaultContractTypeId = contractTypes.find(ct => ct.displayName === 'Umowa o pracę')?.id || contractTypes[0]?.id || '';

  // Roles loaded from DB — used to assign dynamic roles to employees.
  // Editing roles requires both employee-edit AND roles-manage permissions
  // (the latter is the canonical "can manipulate role assignment" permission).
  const canEditEmployees = usePermission('employees.edit');
  const canManageRoles = usePermission('roles.manage');
  const canChangeRole = canEditEmployees && canManageRoles;
  // Deactivating accounts — same permission scope as deleting employees.
  const canDeactivate = usePermission('employees.delete');
  const { data: rolesData } = useQuery(GET_ROLES_BASIC, {
    fetchPolicy: 'cache-and-network',
    skip: !canChangeRole,
  });
  const availableRoles: Array<{ id: string; name: string; isSystem: boolean; permissions: string[] }> = rolesData?.roles || [];
  // The default role for new employees — pick the system "Pracownik" role if available.
  const defaultRoleId = availableRoles.find(r => r.name === 'Pracownik')?.id || '';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    login: '',
    pin: '',
    employeeCode: '',
    role: 'EMPLOYEE',
    roleId: '',
    roleIds: [] as string[],
    departmentId: '',
    positionId: '',
    shiftId: '',
    contractTypeId: defaultContractTypeId,
    disabilityDegreeId: '',
    vacationDays: 26,
    password: '',
    isActive: true,
    hireDate: new Date().toISOString().split('T')[0],
    terminationDate: '',
    autoCloseEnabled: false,
    autoCloseType: 'TIME',
    autoCloseTime: '',
    autoCloseAfterHours: 8
  });

  // Hasło jest potrzebne tylko, gdy któraś z wybranych ról ma auth.adminLogin
  // (czyli pracownik może zalogować się do panelu adminem). Pracownicy z dostępem
  // wyłącznie do tabletu logują się PIN-em — hasła nie potrzebują.
  const requiresPassword = formData.roleIds.some((rid) => {
    const r = availableRoles.find((ar) => ar.id === rid);
    return !!r?.permissions?.includes('auth.adminLogin');
  });

  useEffect(() => {
    if (employee) {
      // Edit mode - show actual shift assignment (empty if none)
      const initialRoleIds = (employee.roleIds && employee.roleIds.length > 0)
        ? employee.roleIds
        : (employee.roleId ? [employee.roleId] : []);
      setFormData({
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        login: employee.login,
        pin: employee.pin,
        employeeCode: employee.employeeCode || '',
        role: employee.role,
        roleId: employee.roleId || '',
        roleIds: initialRoleIds,
        departmentId: employee.departmentId || '',
        positionId: employee.position?.id || '',
        shiftId: employee.shift?.id || '', // Empty if no shift assigned
        contractTypeId: employee.contractType?.id || defaultContractTypeId,
        disabilityDegreeId: employee.disabilityDegreeId || employee.disabilityDegree?.id || defaultDisabilityDegreeId,
        vacationDays: (employee as any).vacationDays || 26,
        password: '',
        isActive: employee.isActive,
        hireDate: employee.hireDate.split('T')[0],
        terminationDate: employee.terminationDate ? employee.terminationDate.split('T')[0] : '',
        autoCloseEnabled: employee.autoCloseEnabled || false,
        autoCloseType: employee.autoCloseType || 'TIME',
        autoCloseTime: employee.autoCloseTime || '',
        autoCloseAfterHours: employee.autoCloseAfterHours || 8
      });
    } else {
      // Create mode - reset form - default to "use default shift"
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        login: '',
        pin: '',
        employeeCode: '',
        role: 'EMPLOYEE',
        roleId: defaultRoleId,
        roleIds: defaultRoleId ? [defaultRoleId] : [],
        departmentId: '',
        positionId: '',
        shiftId: '', // Empty = use default shift
        contractTypeId: defaultContractTypeId,
        disabilityDegreeId: defaultDisabilityDegreeId,
        vacationDays: 26,
        password: '',
        isActive: true,
        hireDate: new Date().toISOString().split('T')[0],
        terminationDate: '',
        autoCloseEnabled: false,
        autoCloseType: 'TIME',
        autoCloseTime: '',
        autoCloseAfterHours: 8
      });
    }
    setFormError('');
  }, [employee, open, shifts, defaultRoleId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validate PIN
    if (formData.pin.length !== 4) {
      setFormError('PIN musi składać się z dokładnie 4 cyfr');
      return;
    }

    if (!/^\d{4}$/.test(formData.pin)) {
      setFormError('PIN może zawierać tylko cyfry');
      return;
    }

    // Validate terminationDate >= hireDate
    if (formData.terminationDate && formData.hireDate && formData.terminationDate < formData.hireDate) {
      setFormError('Data zakończenia zatrudnienia nie może być wcześniejsza niż data zatrudnienia');
      return;
    }

    // Validate password — wymagane dla pracowników, których rola pozwala logować się do panelu (auth.adminLogin).
    if (requiresPassword) {
      if (!employee) {
        // Nowy pracownik: hasło wymagane
        if (!formData.password || formData.password.trim().length < 6) {
          setFormError('Hasło dla pracownika z dostępem do panelu musi mieć co najmniej 6 znaków');
          return;
        }
      } else {
        // Edycja: walidujemy tylko, jeśli hasło zostało wpisane
        if (formData.password && formData.password.trim().length > 0 && formData.password.trim().length < 6) {
          setFormError('Hasło musi mieć co najmniej 6 znaków');
          return;
        }
      }
    } else if (formData.password && formData.password.trim().length > 0 && formData.password.trim().length < 6) {
      // Jeśli rola nie wymaga, ale ktoś jednak wpisał krótkie hasło — odrzuć (długość zawsze min 6).
      setFormError('Hasło musi mieć co najmniej 6 znaków');
      return;
    }

    try {
      setSaving(true);

      // Normalize empty terminationDate → null for GraphQL DateTime scalar
      const payload = {
        ...formData,
        terminationDate: formData.terminationDate ? formData.terminationDate : null,
      };

      // shiftId is sent as-is: empty string means "use default shift"
      await onSave(employee?.id || null, payload);

      // Success toast
      toast.success(employee
        ? `Pracownik ${formData.firstName} ${formData.lastName} został zaktualizowany`
        : `Pracownik ${formData.firstName} ${formData.lastName} został dodany`
      );

      onClose();
    } catch (error: any) {
      const errorMessage = error.message || 'Wystąpił błąd podczas zapisywania pracownika';
      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="bg-background px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            {employee ? `Edytuj: ${employee.firstName} ${employee.lastName}` : 'Dodaj nowego pracownika'}
          </DialogTitle>
          <DialogDescription>
            {employee ? 'Zaktualizuj dane pracownika' : 'Wypełnij formularz aby dodać pracownika do systemu'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <form id="employee-form" onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="text-red-500 text-sm bg-red-50 p-3 rounded-md border border-red-200">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">Imię <span className="text-red-500">*</span></Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="lastName">Nazwisko <span className="text-red-500">*</span></Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="login">Login <span className="text-red-500">*</span></Label>
              <Input
                id="login"
                value={formData.login}
                onChange={(e) => setFormData({...formData, login: e.target.value})}
                placeholder="Unikalny login użytkownika"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Login musi być unikalny (może być też adresem email)
              </p>
            </div>
            <div>
              <Label htmlFor="pin">PIN (4 cyfry) <span className="text-red-500">*</span></Label>
              <Input
                id="pin"
                type="password"
                maxLength={4}
                placeholder="Wprowadź unikalny PIN"
                value={formData.pin}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setFormData({...formData, pin: value});
                  }
                }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                PIN musi być unikalny i składać się z 4 cyfr
              </p>
            </div>
            <div>
              <Label htmlFor="employeeCode">Kod pracownika</Label>
              <Input
                id="employeeCode"
                value={formData.employeeCode}
                onChange={(e) => setFormData({...formData, employeeCode: e.target.value})}
                placeholder="np. 001"
              />
              <p className="text-xs text-gray-500 mt-1">
                Opcjonalny kod identyfikacyjny pracownika
              </p>
            </div>
            {canChangeRole && (
              <div className="md:col-span-2">
                <Label>Role</Label>
                <div className="border border-input rounded-md bg-background p-3 space-y-2 max-h-48 overflow-y-auto">
                  {availableRoles.length === 0 && (
                    <p className="text-sm text-gray-500">Ładowanie ról...</p>
                  )}
                  {availableRoles.map((r) => {
                    const checked = formData.roleIds.includes(r.id);
                    return (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...formData.roleIds, r.id]
                              : formData.roleIds.filter((id) => id !== r.id);
                            // Derive legacy single-role mirror for password-required logic.
                            const selectedRoles = availableRoles.filter((ar) => next.includes(ar.id));
                            let legacyRole: string = 'EMPLOYEE';
                            for (const sr of selectedRoles) {
                              const lower = sr.name.toLowerCase();
                              if (lower.includes('admin') || lower.includes('właścicie')) {
                                legacyRole = 'ADMIN';
                                break;
                              }
                              if (lower.includes('kierown') || lower.includes('manager')) {
                                legacyRole = 'MANAGER';
                              }
                            }
                            setFormData({
                              ...formData,
                              roleIds: next,
                              roleId: next[0] || '',
                              role: legacyRole,
                            });
                          }}
                        />
                        <span>{r.name}{r.isSystem ? ' (systemowa)' : ''}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Pracownik dostaje sumę uprawnień ze wszystkich zaznaczonych ról. Można łączyć np. „Kierownik" + „Lider projektu".
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="departmentId">Dział</Label>
              <select
                id="departmentId"
                value={formData.departmentId}
                onChange={(e) => setFormData({...formData, departmentId: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              >
                <option value="">Brak działu</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="positionId">Stanowisko</Label>
              <select
                id="positionId"
                value={formData.positionId}
                onChange={(e) => setFormData({...formData, positionId: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Wybierz stanowisko</option>
                {positions.map((position) => (
                  <option key={position.id} value={position.id}>
                    {position.name} {position.department && `(${position.department})`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="shiftId">Zmianowość</Label>
              <select
                id="shiftId"
                value={formData.shiftId}
                onChange={(e) => setFormData({...formData, shiftId: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Brak (użyj domyślnej)</option>
                {shifts.map((shift) => (
                  <option key={shift.id} value={shift.id}>
                    {shift.name}{shift.isDefault ? ' (domyślna)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                "Brak" oznacza że system użyje zmiany oznaczonej jako domyślna
              </p>
            </div>
            <div>
              <Label htmlFor="contractTypeId">Rodzaj umowy <span className="text-red-500">*</span></Label>
              <select
                id="contractTypeId"
                value={formData.contractTypeId}
                onChange={(e) => setFormData({...formData, contractTypeId: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              >
                {contractTypes.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.displayName}{ct.hasOvertimePay ? '' : ' (bez nadgodzin)'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="disabilityDegreeId">Stopień niepełnosprawności <span className="text-red-500">*</span></Label>
              <select
                id="disabilityDegreeId"
                value={formData.disabilityDegreeId}
                onChange={(e) => setFormData({...formData, disabilityDegreeId: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              >
                {disabilityDegrees
                  .filter(d => d.isActive || d.id === formData.disabilityDegreeId)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({Number(d.maxWorkHoursPerDay)}h/dzień, {d.breakDurationMinutes + d.additionalBreakMinutes} min przerwy)
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <Label htmlFor="vacationDays">Dni urlopowe (rocznie)</Label>
              <Input
                id="vacationDays"
                type="number"
                min="0"
                max="365"
                value={formData.vacationDays}
                onChange={(e) => setFormData({...formData, vacationDays: parseInt(e.target.value) || 26})}
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hireDate">Data zatrudnienia</Label>
                <Input
                  id="hireDate"
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => setFormData({...formData, hireDate: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="terminationDate">Data zakończenia zatrudnienia</Label>
                <Input
                  id="terminationDate"
                  type="date"
                  value={formData.terminationDate}
                  min={formData.hireDate || undefined}
                  onChange={(e) => setFormData({...formData, terminationDate: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Opcjonalnie. Ostatni dzień pracy (włącznie). Po tej dacie nie będzie można dodawać urlopów ani zdarzeń dla pracownika.
                </p>
              </div>
            </div>
            {canDeactivate && employee && (
              <div className="col-span-full">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Konto aktywne
                  </Label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Wyłączenie konta zablokuje możliwość logowania pracownika, zachowując jego dane historyczne
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="password">
                {employee ? 'Nowe hasło (opcjonalnie)' : 'Hasło'}
                {!employee && requiresPassword && <span className="text-red-500"> *</span>}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={employee ? 'Pozostaw puste, aby nie zmieniać' : (requiresPassword ? 'Wprowadź hasło (min. 6 znaków)' : 'Niewymagane — pracownik loguje się tylko PIN-em na tablecie')}
                required={!employee && requiresPassword}
                disabled={!requiresPassword && !employee}
              />
              <p className="text-xs text-gray-500 mt-1">
                {requiresPassword
                  ? (employee
                      ? 'Hasło do logowania do panelu (login/email + hasło). Pozostaw puste, jeśli nie chcesz zmieniać.'
                      : 'Wybrane role pozwalają zalogować się do panelu — hasło jest wymagane.')
                  : 'Wybrane role nie dają dostępu do panelu. Ten pracownik loguje się wyłącznie PIN-em na tablecie, więc hasło nie jest potrzebne.'}
              </p>
            </div>
          </div>

          {/* Automatyczne zamykanie dnia pracy */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Automatyczne zamykanie dnia pracy</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="autoCloseEnabled"
                    checked={formData.autoCloseEnabled}
                    onChange={(e) => setFormData({...formData, autoCloseEnabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="autoCloseEnabled" className="cursor-pointer">
                    Włącz automatyczne zamykanie dnia pracy
                  </Label>
                </div>
              </div>

              {formData.autoCloseEnabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="autoCloseType">Typ zamykania</Label>
                    <select
                      id="autoCloseType"
                      value={formData.autoCloseType}
                      onChange={(e) => setFormData({...formData, autoCloseType: e.target.value})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="TIME">O określonej godzinie</option>
                      <option value="HOURS">Po określonej liczbie godzin</option>
                    </select>
                  </div>

                  {formData.autoCloseType === 'TIME' ? (
                    <div>
                      <Label htmlFor="autoCloseTime">Godzina zamknięcia (HH:MM)</Label>
                      <Input
                        id="autoCloseTime"
                        type="time"
                        value={formData.autoCloseTime}
                        onChange={(e) => setFormData({...formData, autoCloseTime: e.target.value})}
                        required={formData.autoCloseEnabled && formData.autoCloseType === 'TIME'}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="autoCloseAfterHours">Liczba godzin od wejścia</Label>
                      <Input
                        id="autoCloseAfterHours"
                        type="number"
                        min="0.5"
                        max="24"
                        step="0.5"
                        value={formData.autoCloseAfterHours}
                        onChange={(e) => setFormData({...formData, autoCloseAfterHours: parseFloat(e.target.value) || 8})}
                        required={formData.autoCloseEnabled && formData.autoCloseType === 'HOURS'}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Vacation stats and recent leaves - only in edit mode */}
          {employee && <EmployeeLeaveSection employeeId={employee.id} />}
          </form>
        </div>

        <DialogFooter className="bg-background px-6 pb-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Anuluj
          </Button>
          <Button type="submit" form="employee-form" disabled={saving}>
            {saving ? 'Zapisywanie...' : (employee ? 'Zapisz zmiany' : 'Dodaj pracownika')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Leave section sub-component
const LEAVE_TYPES_MAP: Record<string, { label: string; color: string }> = {
  VACATION: { label: 'Urlop wypoczynkowy', color: 'bg-green-100 text-green-800' },
  SICK_LEAVE: { label: 'Zwolnienie lekarskie', color: 'bg-red-100 text-red-800' },
  PERSONAL: { label: 'Urlop okolicznościowy', color: 'bg-blue-100 text-blue-800' },
  MATERNITY: { label: 'Urlop macierzyński', color: 'bg-pink-100 text-pink-800' },
  PATERNITY: { label: 'Urlop ojcowski', color: 'bg-purple-100 text-purple-800' },
  UNPAID: { label: 'Urlop bezpłatny', color: 'bg-gray-100 text-gray-800' },
  OTHER: { label: 'Inny', color: 'bg-yellow-100 text-yellow-800' },
};

const LEAVE_STATUS_MAP: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Oczekujący', color: 'bg-yellow-100 text-yellow-800' },
  APPROVED: { label: 'Zatwierdzony', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Odrzucony', color: 'bg-red-100 text-red-800' },
  CANCELLED: { label: 'Anulowany', color: 'bg-gray-100 text-gray-800' },
};

const EmployeeLeaveSection: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const { data: statsData } = useQuery(GET_VACATION_STATS, {
    variables: { employeeId, year: currentYear },
    fetchPolicy: 'cache-and-network',
  });

  const { data: leavesData } = useQuery(GET_EMPLOYEE_LEAVES, {
    variables: { employeeId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: leaveTypesData } = useQuery(GET_LEAVE_TYPES, {
    fetchPolicy: 'cache-first',
  });

  const leaveTypesMap = React.useMemo(() => {
    const map: Record<string, { label: string; color: string }> = { ...LEAVE_TYPES_MAP };
    (leaveTypesData?.leaveTypes || [])
      .filter((t: any) => t.isActive)
      .forEach((t: any) => {
        if (!map[t.code]) {
          map[t.code] = { label: t.displayName, color: 'bg-yellow-100 text-yellow-800' };
        }
      });
    return map;
  }, [leaveTypesData]);

  const stats = statsData?.vacationStats;
  const recentLeaves = (leavesData?.employeeLeaves || []).slice(0, 5);

  if (!stats && recentLeaves.length === 0) return null;

  const usedPercentage = stats ? Math.min(100, (stats.usedDays / stats.totalDays) * 100) : 0;
  const pendingPercentage = stats ? Math.min(100 - usedPercentage, (stats.pendingDays / stats.totalDays) * 100) : 0;

  return (
    <div className="border-t pt-4 mt-4">
      <h3 className="text-lg font-semibold mb-4">Nieobecności ({currentYear})</h3>

      {stats && (
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Wykorzystano {stats.usedDays} z {stats.totalDays} dni
              {stats.pendingDays > 0 && ` (${stats.pendingDays} oczekujących)`}
            </span>
            <span className="font-medium">{stats.availableDays} dostępnych</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full flex">
              <div
                className="bg-primary transition-all"
                style={{ width: `${usedPercentage}%` }}
              />
              {pendingPercentage > 0 && (
                <div
                  className="bg-amber-400 transition-all"
                  style={{ width: `${pendingPercentage}%` }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {recentLeaves.length > 0 && (
        <div className="space-y-2">
          {recentLeaves.map((leave: any) => {
            const typeConfig = leaveTypesMap[leave.leaveType] || { label: leave.leaveType, color: '' };
            const statusConfig = LEAVE_STATUS_MAP[leave.status] || { label: leave.status, color: '' };
            return (
              <div key={leave.id} className="flex items-center justify-between text-sm py-1.5 border-b border-dashed last:border-0">
                <div className="flex items-center gap-2">
                  <span>{typeConfig.label}</span>
                  <span className="text-muted-foreground">
                    {new Date(leave.startDate).toLocaleDateString('pl-PL')} — {new Date(leave.endDate).toLocaleDateString('pl-PL')}
                  </span>
                </div>
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              </div>
            );
          })}
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => navigate(`/leaves?employee=${employeeId}`)}
          >
            Zobacz wszystkie &rarr;
          </button>
        </div>
      )}
    </div>
  );
};
